import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  Save, Clock, FileText, Volume2, Pause, Square,
  Loader2, CheckCircle, MoreVertical
} from 'lucide-react';
import tts from '../lib/tts';
import { updateDraft, logTrackEvent, getAccommodations, submitDraft, updateDraftStage } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { toast } from 'sonner';
import CoachPanel from './CoachPanel';
import GoogleDriveExport from './GoogleDriveExport';
import { DemoChip } from './ui/demo-chip';
import { saveDraftLocally, getLocalDraft, markSynced } from '../lib/offlineStorage';
import { initSync } from '../lib/syncManager';
import OfflineIndicator from './OfflineIndicator';

// Quill modules configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    ['blockquote'],
    ['clean']
  ]
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'indent', 'blockquote'
];

// Writing process stages — a quiet chip the student sets and the coach reads,
// not a widget the student has to operate.
const STAGES = [
  { key: 'prewrite', label: 'Plan' },
  { key: 'draft', label: 'Draft' },
  { key: 'revise', label: 'Revise' },
  { key: 'edit', label: 'Edit' },
  { key: 'publish', label: 'Final' },
];

const StageChip = ({ draftId, currentStage, onStageChange, disabled }) => {
  const [saving, setSaving] = useState(false);
  const handleChange = async (e) => {
    const next = e.target.value;
    setSaving(true);
    try {
      await updateDraftStage(draftId, next);
      onStageChange(next);
    } catch {
      toast.error('Could not change stage. Try again.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="flex items-center gap-2" data-testid="stage-chip">
      <span className="text-slate-500 text-xs uppercase tracking-wide">Stage</span>
      <select
        value={currentStage}
        onChange={handleChange}
        disabled={disabled || saving}
        className="bg-slate-900 border border-slate-600 rounded-lg px-2 py-1 text-sm text-white focus:outline-none focus:ring-2 focus:ring-flow-blue disabled:opacity-50"
        aria-label="Writing stage"
      >
        {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
      </select>
    </div>
  );
};

export const DraftSpace = ({
  draft,
  assignment,
  onUpdate,
  showFeedback = true,
  // Teacher "see what students see" preview: renders the full student editor
  // with persistence, stage changes, and submission disabled.
  previewMode = false
}) => {
  const { profile } = useAuth();
  const [content, setContent] = useState(draft?.content || '');
  const [wordCount, setWordCount] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [accommodations, setAccommodations] = useState(null);
  const [ttsSpeaking, setTtsSpeaking] = useState(false);
  const [ttsPaused, setTtsPaused] = useState(false);
  const [currentStage, setCurrentStage] = useState(draft?.stage || 'prewrite');
  const [offlineRecoveryPending, setOfflineRecoveryPending] = useState(false);
  const [offlineContent, setOfflineContent] = useState(null);
  const [showMenu, setShowMenu] = useState(false);

  const autoSaveTimerRef = useRef(null);
  const lastContentRef = useRef(content);

  // Milestone nudges (master spec Phase 5 item 3): lightweight client-side
  // triggers offering an OPT-IN coach check-in. Each fires at most once per
  // editor session; nothing is ever sent unless the student accepts.
  const [nudge, setNudge] = useState(null);
  const firedNudgesRef = useRef(new Set());
  const sessionStartRef = useRef(Date.now());
  const lastInputRef = useRef(Date.now());
  const wordCountRef = useRef(0);

  // Declared here, above the nudge effects: a dependency array is evaluated
  // during render, so an effect listing isSubmitted must come after it.
  const isSubmitted = draft?.status === 'submitted';

  // Calculate word count. Quill serializes normalized spaces as &nbsp;
  // entities in root.innerHTML; the raw entity text contains no /\s/
  // characters, so without decoding them a loaded draft counts as 1 word.
  useEffect(() => {
    const text = content
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;|\u00a0/g, ' ')
      .trim();
    const words = text ? text.split(/\s+/).length : 0;
    setWordCount(words);
    wordCountRef.current = words;
    lastInputRef.current = Date.now();
  }, [content]);

  // Nudge trigger 1: first 100 words (fires on the CROSSING, once). A draft
  // that already opens at 100+ words is not a crossing — mark it fired on
  // mount (wordCountRef is accurate here: the word-count effect runs first).
  useEffect(() => {
    if (!firedNudgesRef.current.has('init')) {
      firedNudgesRef.current.add('init');
      if (wordCountRef.current >= 100) firedNudgesRef.current.add('first-100');
      return;
    }
    if (previewMode || isSubmitted || profile?.role === 'teacher') return;
    if (wordCount >= 100 && !firedNudgesRef.current.has('first-100')) {
      firedNudgesRef.current.add('first-100');
      setNudge(n => n || {
        id: 'first-100',
        label: 'You just passed 100 words — real momentum! Want a quick check-in from Drafty?',
        question: 'I just passed 100 words. Can you give me a quick check-in on how my draft is going so far?',
      });
    }
  }, [wordCount, previewMode, isSubmitted, profile?.role]);

  // Nudge triggers 2 + 3: 10 minutes of writing; 3+ minute stall mid-draft.
  // A 30s tick keeps this cheap; refs avoid stale closures.
  useEffect(() => {
    if (previewMode || isSubmitted || profile?.role === 'teacher') return;
    const tick = setInterval(() => {
      const now = Date.now();
      if (now - sessionStartRef.current >= 10 * 60 * 1000 && !firedNudgesRef.current.has('ten-min')) {
        firedNudgesRef.current.add('ten-min');
        setNudge(n => n || {
          id: 'ten-min',
          label: "Ten minutes of writing — nice focus. Want Drafty to check in on your progress?",
          question: "I've been writing for about ten minutes. Can you check in on my progress and help me decide what to focus on next?",
        });
        return;
      }
      if (
        wordCountRef.current > 0 &&
        now - lastInputRef.current >= 3 * 60 * 1000 &&
        !firedNudgesRef.current.has('stall')
      ) {
        firedNudgesRef.current.add('stall');
        setNudge(n => n || {
          id: 'stall',
          label: 'Stuck for a minute? Drafty can ask you something to get you moving again.',
          question: "I'm feeling a little stuck. Can you ask me a question to help me get going again?",
        });
      }
    }, 30000);
    return () => clearInterval(tick);
  }, [previewMode, isSubmitted, profile?.role]);

  // Load accommodations. Teachers have none (accommodations are keyed to
  // students), and the backend 403s a teacher reading their own id — so a
  // teacher previewing the student view must skip the fetch entirely.
  useEffect(() => {
    const loadAccommodations = async () => {
      if (profile?.id && profile.role !== 'teacher') {
        try {
          const acc = await getAccommodations(profile.id);
          setAccommodations(acc);
        } catch (e) {
          console.error('Failed to load accommodations:', e);
        }
      }
    };
    loadAccommodations();
  }, [profile]);

  // Check for offline recovery on mount
  useEffect(() => {
    const checkOfflineRecovery = async () => {
      if (!draft?.id) return;
      try {
        const local = await getLocalDraft(draft.id);
        if (local && !local.synced && local.localUpdatedAt) {
          const serverTime = draft.last_saved || draft.created_at || '';
          if (local.localUpdatedAt > serverTime) {
            setOfflineContent(local.content);
            setOfflineRecoveryPending(true);
          }
        }
      } catch (e) {
        console.error('Offline recovery check failed:', e);
      }
    };
    checkOfflineRecovery();
    initSync();
  }, [draft?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save functionality — saves to API (if online) AND IndexedDB (always)
  const saveContent = useCallback(async () => {
    if (!draft?.id || isSubmitted || content === lastContentRef.current) return;

    setIsSaving(true);

    // Always save locally first
    try {
      await saveDraftLocally(draft.id, {
        content,
        word_count: wordCount,
        stage: currentStage,
      });
    } catch (e) {
      console.error('Local save failed:', e);
    }

    try {
      await updateDraft(draft.id, content, wordCount);

      // Log track event for revision history (full content for playback reconstruction)
      const diff = content.length - (lastContentRef.current?.length || 0);
      const hasSignificantChange = Math.abs(diff) > 20;
      if (hasSignificantChange) {
        await logTrackEvent(draft.id, diff > 0 ? 'addition' : 'deletion', content, wordCount);
      }

      lastContentRef.current = content;
      setLastSaved(new Date());
      // Server accepted the save — the local copy is no longer "unsynced",
      // otherwise every reopen shows a false offline-recovery banner
      try { await markSynced(draft.id); } catch {}
      onUpdate?.({ content, wordCount });
    } catch (error) {
      console.error('Save error (saved locally):', error);
      // Don't show error toast — offline indicator handles this.
      // Keep lastSaved untouched so the UI doesn't claim a save that failed;
      // the sync manager pushes the local copy when the connection returns.
      lastContentRef.current = content;
    } finally {
      setIsSaving(false);
    }
  }, [draft?.id, isSubmitted, content, wordCount, currentStage, onUpdate]);

  // Set up auto-save timer
  useEffect(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      saveContent();
    }, 2000); // Auto-save after 2 seconds of inactivity

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [content, saveContent]);

  // TTS state polling
  useEffect(() => {
    if (!accommodations?.audio_reader || !tts.isSupported()) return;
    const interval = setInterval(() => {
      setTtsSpeaking(tts.isSpeaking());
      setTtsPaused(tts.isPaused());
    }, 200);
    return () => clearInterval(interval);
  }, [accommodations?.audio_reader]);

  // Stop TTS on unmount
  useEffect(() => {
    return () => tts.stop();
  }, []);

  // Stop TTS when content changes while speaking
  useEffect(() => {
    if (ttsSpeaking) {
      tts.stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  // Strip HTML for TTS
  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // TTS handlers
  const handleTtsPlay = () => {
    if (ttsPaused) {
      tts.resume();
    } else {
      const text = stripHtml(content);
      if (text.trim()) {
        tts.speak(text);
      }
    }
  };

  const handleTtsPause = () => {
    tts.pause();
  };

  const handleTtsStop = () => {
    tts.stop();
  };

  // Submit draft
  const handleSubmit = async () => {
    if (!draft?.id) return;

    // Save first
    await saveContent();

    try {
      await submitDraft(draft.id);
      toast.success('Nice work! Your draft has been submitted for review.');
      onUpdate?.({ status: 'submitted' });
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit. Please try again.');
    }
  };

  // Determine writing type from assignment standards
  const getWritingType = () => {
    const stds = assignment?.ccss_standards || [];
    for (const s of stds) {
      if (s.startsWith('W.9-10.1')) return 'argumentative';
      if (s.startsWith('W.9-10.2')) return 'informational';
      if (s.startsWith('W.9-10.3')) return 'narrative';
    }
    return 'argumentative'; // default
  };
  const writingType = getWritingType();

  // Insert text at end of editor content (coach panel scaffolds use this)
  const handleInsertText = (text) => {
    setContent(prev => prev + `<p>${text}</p>`);
  };

  const coachPanel = (
    <CoachPanel
      draft={draft}
      assignment={assignment}
      content={content}
      currentStage={currentStage}
      writingType={writingType}
      profile={profile}
      onInsert={handleInsertText}
      nudge={nudge}
      onNudgeDone={() => setNudge(null)}
    />
  );

  return (
    <div className="h-full">
      {/* Offline Recovery Banner */}
      {offlineRecoveryPending && offlineContent && (
        <div className="bg-gold-bright/10 border border-gold-bright/30 rounded-xl px-4 py-3 mb-4 flex items-center justify-between">
          <p className="text-gold-bright text-sm">You have unsaved changes from an offline session.</p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setContent(offlineContent);
                setOfflineRecoveryPending(false);
                setOfflineContent(null);
                toast.success('Offline changes restored!');
              }}
              className="bg-gold-bright text-slate-900 px-3 py-1 rounded-lg text-sm font-medium"
            >
              Restore
            </button>
            <button
              onClick={() => {
                setOfflineRecoveryPending(false);
                setOfflineContent(null);
              }}
              className="text-slate-400 hover:text-white px-3 py-1 rounded-lg text-sm"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-4 h-full">
        {/* Zone 2: the page */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Toolbar */}
          <div className="flex items-center justify-between bg-slate-800 rounded-t-xl px-4 py-3 border-b border-slate-700">
            <div className="flex items-center gap-4">
              <StageChip
                draftId={draft?.id}
                currentStage={currentStage}
                onStageChange={setCurrentStage}
                disabled={isSubmitted || previewMode}
              />
              <div className="flex items-center gap-2 text-slate-400">
                <FileText size={18} />
                <span className="font-mono text-sm">{wordCount} words</span>
              </div>

              {lastSaved && (
                <div className="hidden sm:flex items-center gap-2 text-slate-500 text-sm">
                  <Clock size={14} />
                  <span>Saved {lastSaved.toLocaleTimeString()}</span>
                </div>
              )}

              {isSaving && (
                <div className="flex items-center gap-2 text-teal-flow">
                  <Loader2 size={14} className="animate-spin" />
                  <span className="text-sm">Saving...</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* TTS Accommodation Buttons */}
              {accommodations?.audio_reader && tts.isSupported() && (
                <div className="flex items-center gap-1 border-r border-slate-700 pr-2">
                  <button
                    data-testid="tts-play-btn"
                    onClick={handleTtsPlay}
                    aria-label="Read draft aloud"
                    title="Read draft aloud"
                    className="bg-flow-blue/20 text-flow-blue-hover hover:bg-flow-blue/30 rounded-md px-2 py-1"
                  >
                    <Volume2 size={16} />
                  </button>
                  {(ttsSpeaking || ttsPaused) && (
                    <>
                      {ttsSpeaking && !ttsPaused && (
                        <button
                          data-testid="tts-pause-btn"
                          onClick={handleTtsPause}
                          aria-label="Pause reading"
                          className="bg-flow-blue/20 text-flow-blue-hover hover:bg-flow-blue/30 rounded-md px-2 py-1"
                        >
                          <Pause size={16} />
                        </button>
                      )}
                      <button
                        data-testid="tts-stop-btn"
                        onClick={handleTtsStop}
                        aria-label="Stop reading"
                        className="bg-flow-blue/20 text-flow-blue-hover hover:bg-flow-blue/30 rounded-md px-2 py-1"
                      >
                        <Square size={16} />
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Overflow menu — Drive export lives here */}
              {!previewMode && (
              <div className="relative">
                <button
                  data-testid="editor-overflow"
                  onClick={() => setShowMenu(v => !v)}
                  className="p-2 hover:bg-slate-700 rounded-lg text-slate-400"
                  aria-label="More options"
                >
                  <MoreVertical size={18} />
                </button>
                {showMenu && (
                  <div className="absolute right-0 top-full mt-1 z-20 bg-slate-800 border border-slate-700 rounded-xl p-2 shadow-xl min-w-[200px]">
                    <GoogleDriveExport draftId={draft?.id} draftTitle={assignment?.title || 'Draft'} />
                  </div>
                )}
              </div>
              )}

              {!isSubmitted && !previewMode && (
                <button
                  data-testid="save-draft-btn"
                  onClick={saveContent}
                  className="btn-teal px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm"
                >
                  <Save size={16} />
                  Save
                </button>
              )}

              {!previewMode && (
              <DemoChip label="Sign in to submit drafts">
                <button
                  data-testid="submit-draft-btn"
                  onClick={handleSubmit}
                  disabled={!content.trim() || isSubmitted}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
                >
                  <CheckCircle size={16} />
                  Submit
                </button>
              </DemoChip>
              )}
            </div>
          </div>

          <OfflineIndicator />

          {/* Submitted Banner */}
          {isSubmitted && (
            <div className="bg-green-600/10 border-b border-green-600/30 px-4 py-2 flex items-center gap-2 text-green-400 text-sm">
              <CheckCircle size={16} />
              Great job finishing your draft! It's been submitted and is ready for your teacher to review.
            </div>
          )}

          {/* Editor */}
          <div className="flex-1 bg-slate-800 rounded-b-xl overflow-hidden">
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              modules={quillModules}
              formats={quillFormats}
              readOnly={currentStage === 'publish' || isSubmitted}
              placeholder="Start writing... Let your ideas flow."
              className="h-full draft-editor"
            />
          </div>
        </div>

        {/* Zone 3: coach panel — sticky beside the writing on md+ */}
        {showFeedback && (
          <div className="hidden md:block w-[380px] flex-shrink-0">
            <div className="sticky top-4 h-[calc(100vh-8rem)]">
              {coachPanel}
            </div>
          </div>
        )}
      </div>

      {/* Coach on small screens — stacked below the editor */}
      {showFeedback && (
        <div className="md:hidden mt-4 max-h-[70vh]">
          {coachPanel}
        </div>
      )}
    </div>
  );
};

export default DraftSpace;
